import { Fragment, useContext, useState, useEffect, useMemo } from 'react';

import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { portalItems } from '../constants/portal';
import { routes } from '../constants/routes';
import PortalContext from '../context/portal/context';
import { Portal, PortalLink, PortalType } from '../enums/portal.enum';
import { useModalState } from '../hooks/useModalState';
import {
  getIsResponsePopulated,
  setResponsePopulated,
} from '../utils/responsePopulatedUtil';

import ModalAlerts from './organisms/Modal/ModalAlerts';

function PortalDropdown() {
  const { selectedPortal, setSelectedPortal } = useContext(PortalContext);
  const [mounted, setMounted] = useState(false);
  const [pendingPortalId, setPendingPortalId] = useState<Portal | null>(null);
  const selectedPortalLogo = mounted
    ? portalItems.find((portal) => portal.id === selectedPortal)?.logo
    : portalItems[0].logo;
  useEffect(() => {
    setMounted(true);
  }, []);

  const { setShowConfirmationModal, showConfirmationModal } = useModalState({});
  const [modalRoute, setModalRoute] = useState<string | null>(null);
  const router = useRouter();

  const countryPortals = useMemo(
    () => portalItems.filter((portal) => portal.type === PortalType.COUNTRY),
    [],
  );

  const curriculumPortals = useMemo(
    () => portalItems.filter((portal) => portal.type === PortalType.CURRICULUM),
    [],
  );

  function openModal(route: string, portalId: Portal) {
    if (getIsResponsePopulated()) {
      setModalRoute(route);
      setPendingPortalId(portalId);
      setShowConfirmationModal(true);
    }
  }

  const handleRedirect = (portalId: Portal) => {
    const portalPath = PortalLink[portalId];
    const isInternationalPortal = portalId === Portal.INTERNATIONAL;
    const finalRoute = isInternationalPortal ? routes.UNIT_PLAN : routes.MAIN;
    const route = `${portalPath}${finalRoute}`;
    if (!getIsResponsePopulated()) {
      router.push(route);
      setSelectedPortal(portalId);
    } else {
      openModal(route, portalId);
    }
  };

  function handleConfirmClose() {
    if (modalRoute && pendingPortalId) {
      router.push(modalRoute);
      setSelectedPortal(pendingPortalId);
      setResponsePopulated(false);
    } else {
      setShowConfirmationModal(false);
    }
  }

  const renderOptions = () => {
    const renderPortalGroup = (title: string, portals: typeof portalItems) => (
      <div className="flex flex-col">
        <span className="px-3 py-1 text-sm font-medium text-gray-600">
          {title}
        </span>
        {portals.map((portal) => {
          const isSelected = selectedPortal === portal.id;

          return (
            <Menu.Item key={portal.id}>
              {() => (
                <button
                  className={clsx(
                    'flex w-full items-center border-blue-300 px-5 py-2 text-start text-sm text-gray-600 duration-100 hover:border-l-4 hover:bg-gray-50 hover:text-gray-800',
                    isSelected && 'bg-gray-50 font-medium',
                  )}
                  onClick={() => handleRedirect(portal.id)}
                >
                  <img
                    alt={portal.label}
                    className="mr-2 rounded-full"
                    height={25}
                    src={portal.logo}
                    width={25}
                  />
                  {portal.label}
                </button>
              )}
            </Menu.Item>
          );
        })}
      </div>
    );

    return (
      <div className="flex flex-col gap-3">
        {renderPortalGroup('Country', countryPortals)}
        {renderPortalGroup('Curriculum', curriculumPortals)}
      </div>
    );
  };

  return (
    <div className="z-40 flex h-full flex-row items-center">
      <Menu
        as="div"
        className="relative inline-block h-full w-auto text-left sm:w-20"
      >
        <div className="h-full sm:pl-3">
          <Menu.Button className="flex h-full w-full flex-row items-center justify-between rounded-full border border-gray-400 p-1 text-xs text-gray-600 ring-blue-300 hover:text-gray-800 hover:ring-2 md:text-lg">
            {mounted && (
              <img
                alt={selectedPortalLogo}
                className="flex h-8 w-8 rounded-full border border-gray-200 sm:h-10 sm:w-10"
                height={32}
                src={selectedPortalLogo}
                width={32}
              />
            )}
            <ChevronDownIcon
              aria-hidden="true"
              className="h-4 w-4 text-[#1C4ED8] sm:h-5 sm:w-5"
            />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-6 z-10 mt-1 w-[200px] rounded-md border border-gray-200 bg-white shadow-lg sm:left-0">
            <div className="flex flex-col py-2">{renderOptions()}</div>
          </Menu.Items>
        </Transition>
      </Menu>
      <ModalAlerts
        handleConfirmClose={handleConfirmClose}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />
    </div>
  );
}

export default PortalDropdown;
